import { apiBase, portalOrigin, portalUrls } from '../config';
import { persistSession, readStoredToken, readStoredUser } from './auth-storage';
import { planAccessForProduct } from './plan-access';

const PENDING_CHECKOUT_KEY = 'protego_pending_checkout';
const RESUME_LOCK_KEY = 'protego_checkout_resuming';
const RESUME_LOCK_TTL_MS = 12_000;

/** Live $197 POA Payment Link — fallback only when API checkout fails. */
const LIVE_POA_STRIPE_URL = 'https://buy.stripe.com/5kQ9AT8ob0Au0wReNH5os01';

/** User already owns this product (exact plan or a higher plan that includes it). */
export function userAlreadyHasPlan(user, planId = 'poa') {
  const access = planAccessForProduct(planId, user);
  return access === 'current' || access === 'included';
}

/** Hard fallback Stripe URL for POA (live Payment Link). */
export function poaStripeFallbackUrl(explicit) {
  if (explicit) return explicit;
  return process.env.REACT_APP_STRIPE_LINK_POA || LIVE_POA_STRIPE_URL;
}

/**
 * Payment Links (buy.stripe.com/<id>) accept client_reference_id + prefilled_email so the
 * webhook can attribute the payment. Hosted session URLs (/c/pay/cs_…) must stay untouched.
 */
export function withPaymentLinkIdentity(url, user = readStoredUser()) {
  if (!url || !user) return url;

  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'buy.stripe.com' || parsed.pathname.startsWith('/c/')) {
      return url;
    }

    // Stripe allows alphanumeric, "-" and "_" only.
    const reference = String(user.id ?? '').replace(/[^A-Za-z0-9_-]/g, '');
    if (reference && !parsed.searchParams.has('client_reference_id')) {
      parsed.searchParams.set('client_reference_id', reference);
    }
    if (user.email && !parsed.searchParams.has('prefilled_email')) {
      parsed.searchParams.set('prefilled_email', user.email);
    }

    return parsed.toString();
  } catch {
    return url;
  }
}

function finishCheckoutNavigation(url, user = readStoredUser()) {
  const target = (url || '').trim();
  if (!target) {
    sessionStorage.removeItem(RESUME_LOCK_KEY);
    return false;
  }
  clearPendingCheckout();
  sessionStorage.removeItem(RESUME_LOCK_KEY);
  stripCheckoutResumeQuery();
  window.location.assign(withPaymentLinkIdentity(target, user));
  return true;
}

/** True while another resume attempt is still in-flight (stale locks expire). */
function acquireResumeLock() {
  const raw = sessionStorage.getItem(RESUME_LOCK_KEY);
  if (raw) {
    const ts = Number(raw);
    if (Number.isFinite(ts) && Date.now() - ts < RESUME_LOCK_TTL_MS) {
      return false;
    }
  }
  sessionStorage.setItem(RESUME_LOCK_KEY, String(Date.now()));
  return true;
}

/** After portal signup, return here and auto-resume Stripe checkout. */
export function checkoutReturnUrl() {
  const url = new URL(`${window.location.origin}/`);
  url.searchParams.set('resume_checkout', '1');
  return url.toString();
}

/** Return to whatever site started checkout (POA / portal / landing). */
export function checkoutReturnOrigin() {
  return window.location.origin;
}

export function authUrlWithReturn(kind = 'signup') {
  const returnTo = window.location.href;
  const base = kind === 'login' ? `${portalOrigin}/login` : `${portalOrigin}/signup`;
  return `${base}?redirect=${encodeURIComponent(returnTo)}`;
}

export function signupUrlWithReturn(returnTo = checkoutReturnUrl()) {
  return `${portalOrigin}/signup?redirect=${encodeURIComponent(returnTo)}`;
}

export function loginUrlWithReturn() {
  return authUrlWithReturn('login');
}

export function savePendingCheckout(checkout) {
  localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(checkout));
  sessionStorage.removeItem(RESUME_LOCK_KEY);
}

export function peekPendingCheckout() {
  const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.plan || parsed?.fallbackUrl) {
      return parsed;
    }
  } catch {
    return { fallbackUrl: raw };
  }

  return { fallbackUrl: raw };
}

export function clearPendingCheckout() {
  localStorage.removeItem(PENDING_CHECKOUT_KEY);
}

/** @deprecated Prefer peek + clear after Stripe navigation starts */
export function takePendingCheckout() {
  const pending = peekPendingCheckout();
  clearPendingCheckout();
  return pending;
}

/** Only true when returning from portal signup/login with explicit intent. */
export function hasExplicitCheckoutResume() {
  return new URLSearchParams(window.location.search).get('resume_checkout') === '1';
}

/**
 * Resume Stripe only for post-auth return (?resume_checkout=1).
 * Pending alone must NEVER auto-redirect — otherwise a normal visit to poa.section3.ai
 * keeps bouncing to Stripe after an abandoned CTA.
 */
export function wantsCheckoutResume() {
  return hasExplicitCheckoutResume();
}

/** Drop stale CTA intent when the user is just browsing (no resume query). */
export function clearStaleCheckoutIntent() {
  if (hasExplicitCheckoutResume()) return;
  clearPendingCheckout();
  sessionStorage.removeItem(RESUME_LOCK_KEY);
}

export function stripCheckoutResumeQuery() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('resume_checkout')) return;
  params.delete('resume_checkout');
  const next = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`
  );
}

export async function createCheckoutSession({
  plan,
  interval = 'monthly',
  returnOrigin = checkoutReturnOrigin(),
}) {
  const token = readStoredToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const res = await fetch(`${apiBase}/api/settings/checkout-session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      plan,
      interval,
      return_origin: returnOrigin,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || 'Could not start checkout');
  }

  if (!data?.url) {
    throw new Error('Missing checkout URL');
  }

  return data;
}

export async function confirmCheckoutSession(sessionId) {
  const token = readStoredToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 25000);

  let res;
  try {
    res = await fetch(`${apiBase}/api/settings/confirm-checkout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ session_id: sessionId }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Payment confirmation timed out. Tap Retry or open the dashboard.');
    }
    throw err;
  } finally {
    window.clearTimeout(timeout);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 422) {
      try {
        const synced = await syncPayments();
        if (synced?.billing?.plan_active) {
          return synced;
        }
      } catch {
        // keep original error
      }
    }
    throw new Error(data?.message || 'Could not confirm payment');
  }

  const billing = data?.billing || {};
  const user = readStoredUser() || {};
  const updated = {
    ...user,
    plan: billing.plan ?? user.plan,
    plan_active: billing.plan_active ?? user.plan_active,
    plan_features: billing.plan_features ?? user.plan_features,
  };
  persistSession(token, updated);

  return data;
}

async function syncPayments() {
  const token = readStoredToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const res = await fetch(`${apiBase}/api/settings/sync-payments`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || 'Could not sync payments');
  }

  const billing = data?.billing || {};
  const user = readStoredUser() || {};
  persistSession(token, {
    ...user,
    plan: billing.plan ?? user.plan,
    plan_active: billing.plan_active ?? user.plan_active,
    plan_features: billing.plan_features ?? user.plan_features,
  });

  return data;
}

/** Always same-tab — popup blockers break post-signup resume. */
export function openStripeCheckout(stripeUrl) {
  window.location.assign(stripeUrl);
}

export async function startCheckout({
  plan,
  interval = 'monthly',
  fallbackUrl,
  returnOrigin = checkoutReturnOrigin(),
  user = readStoredUser(),
}) {
  // Already subscribed / plan included → never send them to Stripe again.
  if (plan && userAlreadyHasPlan(user, plan)) {
    finishCheckoutNavigation(portalUrls.dashboard, user);
    return { skipped: true, reason: 'already_owned', navigated: true };
  }

  // POA: go straight to the live Payment Link (same flow that worked in QA).
  // API Checkout Session is used for other plans; POA product is the Payment Link.
  if (plan === 'poa') {
    finishCheckoutNavigation(poaStripeFallbackUrl(fallbackUrl), user);
    return { skipped: false, usedPaymentLink: true, navigated: true };
  }

  if (plan) {
    try {
      const session = await createCheckoutSession({ plan, interval, returnOrigin });
      finishCheckoutNavigation(session.url, user);
      return { skipped: false, navigated: true };
    } catch (err) {
      const url = poaStripeFallbackUrl(fallbackUrl);
      if (url) {
        finishCheckoutNavigation(url, user);
        return { skipped: false, usedFallback: true, navigated: true };
      }
      throw err;
    }
  }

  if (fallbackUrl) {
    finishCheckoutNavigation(fallbackUrl, user);
    return { skipped: false, usedFallback: true, navigated: true };
  }

  return { skipped: false, navigated: false };
}

/**
 * After signup/login return (?resume_checkout=1): resume Stripe if needed.
 * Returns true when navigation away was started (Stripe or dashboard).
 */
export async function resumePendingCheckoutIfAny(user = readStoredUser()) {
  if (!hasExplicitCheckoutResume()) {
    clearStaleCheckoutIntent();
    return false;
  }

  const pending = peekPendingCheckout() || {
    plan: 'poa',
    interval: 'monthly',
    fallbackUrl: poaStripeFallbackUrl(),
  };

  if (!acquireResumeLock()) {
    // Another tab/call already started — still ensure we leave for Stripe.
    finishCheckoutNavigation(poaStripeFallbackUrl(pending.fallbackUrl), user);
    return true;
  }

  const plan = pending.plan || 'poa';
  if (userAlreadyHasPlan(user, plan)) {
    return finishCheckoutNavigation(portalUrls.dashboard, user);
  }

  try {
    const result = await startCheckout({ ...pending, user });
    if (result?.navigated || result?.skipped) return true;
    return finishCheckoutNavigation(poaStripeFallbackUrl(pending.fallbackUrl), user);
  } catch (err) {
    sessionStorage.removeItem(RESUME_LOCK_KEY);
    throw err;
  }
}
